import { baseUrl } from "../baseUrl";
import { LoadingComponent } from "../components/loading";
import {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  type ReactNode
} from "react";

type AuthContextType = {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  login: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(() => {
    window.location.href = `${baseUrl}/auth/signin`;
  }, []);

  const logout = useCallback(() => {
    window.location.href = `${baseUrl}/auth/signout`;
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${baseUrl}/auth/profile`, {
          method: "GET",
          credentials: "include"
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else if (response.status === 401) {
          const refreshResponse = await fetch(`${baseUrl}/auth/token/refresh`, {
            method: "POST",
            credentials: "include"
          });

          if (refreshResponse.ok) {
            const retryResponse = await fetch(`${baseUrl}/auth/profile`, {
              method: "GET",
              credentials: "include"
            });

            if (retryResponse.ok) {
              setIsAuthenticated(true);
              return;
            }
          }

          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error checking authentication status:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated, setIsAuthenticated, login, logout }),
    [isAuthenticated, login, logout]
  );
  if (isLoading) {
    return <LoadingComponent />;
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuth };
export type { AuthContextType };
